import axios, { type AxiosError, AxiosHeaders } from 'axios';
import type { RuntimeConfig } from '../types/runtime';
import { getImpersonationToken, getUserToken } from '../utils/session';
import { handleSessionExpired } from '../utils/sessionExpiry';

export interface NormalizedApiError {
  name: 'ApiClientError';
  message: string;
  status: number;
  method: string;
  url: string;
  data: unknown;
}

function getRuntimeConfig(): RuntimeConfig {
  if (typeof window === 'undefined') {
    return {};
  }

  return window.mxAppConfig || {};
}

function resolveAuthToken(): string | null {
  return getUserToken() || getImpersonationToken();
}

function normalizeError(error: unknown): NormalizedApiError {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    return {
      name: 'ApiClientError',
      message: axiosError.message || 'Request failed',
      status: axiosError.response?.status || 0,
      method: (axiosError.config?.method || 'get').toUpperCase(),
      url: axiosError.config?.url || '',
      data: axiosError.response?.data,
    };
  }

  if (error instanceof Error) {
    return {
      name: 'ApiClientError',
      message: error.message,
      status: 0,
      method: 'GET',
      url: '',
      data: null,
    };
  }

  return {
    name: 'ApiClientError',
    message: 'Unexpected request error',
    status: 0,
    method: 'GET',
    url: '',
    data: null,
  };
}

export const httpClient = axios.create({
  baseURL: getRuntimeConfig().endPoint,
  headers: {
    'Content-Type': 'application/json',
  },
});

httpClient.interceptors.request.use(config => {
  const runtimeConfig = getRuntimeConfig();
  const token = resolveAuthToken();
  const headers = AxiosHeaders.from(config.headers || {});

  if (runtimeConfig.soapPharmacy) {
    headers.set('Pharmacy', runtimeConfig.soapPharmacy);
  }

  if (token) {
    headers.set('Authorization', token);
  }

  config.headers = headers;

  return config;
});

httpClient.interceptors.response.use(
  response => response,
  (error: unknown) => {
    const normalized = normalizeError(error);

    if (normalized.status === 401) {
      handleSessionExpired();
    }

    return Promise.reject(normalized);
  }
);
